import { ChangeEvent, HTMLInputAutoCompleteAttribute } from "react";
import {
    ControllerRenderProps,
    FieldValues,
    Path,
    PathValue,
    useFormContext
} from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

type PromotionFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    placeholder?: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    ref?: React.Ref<HTMLInputElement>;
};

const MAX_LENGTH = 30;
const PROMOTION_REGEX = "^[a-zA-Z0-9-()]+$";

const PromotionField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    placeholder,
    autoComplete,
    disabled,
    hideLabel,
    hideMicroCopy,
    optionalIndication,
    requiredIndication,
    ref,
    test
}: PromotionFieldProps<T>) => {
    const { setValue } = useFormContext<T>();
    const { handleOnAutofill } = useAutofill<T>(name);

    const handleChange = (
        oldValue: PathValue<T, Path<T>>,
        e: ChangeEvent<HTMLInputElement>,
        onChangeCB: ControllerRenderProps<T, Path<T>>["onChange"]
    ) => {
        const { value = "", validity } = e.currentTarget;
        const parsedValue = value;

        if (!value && !validity.valid) {
            e.preventDefault();
            setValue(name, oldValue);
            return;
        }

        const hasPassedMaxLength = parsedValue.toString().length > MAX_LENGTH;
        if (hasPassedMaxLength) {
            e.preventDefault();
            return;
        }
        if (parsedValue && !parsedValue.match(new RegExp(PROMOTION_REGEX))) {
            e.preventDefault();
            return;
        }

        onChangeCB?.(e);
    };

    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
            optionalIndication={optionalIndication}
            requiredIndication={requiredIndication}
        >
            {({ field, fieldState }) => {
                return (
                    <Input
                        {...field}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleChange(field.value, e, field.onChange)
                        }
                        type="text"
                        inputMode="text"
                        id={name}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        disabled={disabled}
                        data-test={test}
                        maxLength={MAX_LENGTH}
                        pattern={PROMOTION_REGEX}
                        ref={ref}
                        onAutofill={handleOnAutofill}
                    />
                );
            }}
        </BaseField>
    );
};

export { PromotionField };
